import {
  Order,
  OrderResponse,
  OrderResponseDetail,
  VehicleShort,
} from '../../../src/api/cart/interfaces'
import { OrderHeaderProps } from './lib/components/order/OrderHeader'
import { PartDetail } from './lib/components/order/PartsTableContent'
import { VehicleInfoProps } from '../../../src/features/SummaryPanel/Interfaces'
import { OrderStatus } from '../../../src/store/orderHistory/orderHistoryStore'
import { StoreInstances } from '../../../src/store/StoreInstancesContainer'
import { LaborItem } from '../../../src/store/models/LaborModel'
import { IBundleProduct } from 'cart'
import { StorageKeys } from 'common'
import {
  formatVehicleDisplayWithOutEngine,
  formatVehicleShortDisplayWithOutEngine,
} from 'src/helpers/utils'

const calcPartSubTotal = (part: PartDetail): number => {
  const { bundled, bundlePrice, yourCost, corePrice, qty } = part
  const cost = bundled && (bundlePrice ?? 0) > 0 ? (bundlePrice ?? 0) : yourCost
  return (cost + (corePrice ?? 0)) * qty
}

export const calcOrderSubTotal = (
  parts: Array<PartDetail>,
  laborItems: Array<LaborItem>
): number => {
  const laborSubtotal =
    StoreInstances.laborStore.calculateLaborCostFromItems(laborItems)
  const partsSubtotal = parts.reduce(
    (acc, item) => acc + calcPartSubTotal(item),
    0
  )
  return laborSubtotal + partsSubtotal
}

export const getOrderHeader = (
  vehicle: VehicleShort,
  poNumber: string,
  noteToStore: string,
  comment: string,
  order: Order
): OrderHeaderProps => {
  const vehicleName = formatVehicleShortDisplayWithOutEngine(vehicle)
  const { orderNumber, deliveryDetail } = order
  const orderConfirmationNumber = order.parts[0].orderConfirmationNbr
  const orderType = deliveryDetail?.deliveryMethod
  const orderTransport = deliveryDetail?.carrier
  const customerName = deliveryDetail?.name
  const shipDestination = deliveryDetail?.address?.line1
    ? `${deliveryDetail?.address?.line1} ${deliveryDetail?.address?.line2}, ${deliveryDetail?.address?.city}, ${deliveryDetail?.address?.region}, ${deliveryDetail?.address?.postalCode} `
    : undefined

  return {
    vehicleName,
    orderNumber,
    orderType: orderType || '',
    transport: orderTransport || '',
    orderStatus: OrderStatus.ORDERED,
    poNumber,
    notesToStore: noteToStore,
    shipDestination: shipDestination || '',
    customerName: customerName || '',
    personalNote: comment || '',
    orderConfirmationNumber: orderConfirmationNumber || '',
  }
}

export const getPartDetail = (part: IBundleProduct): PartDetail => {
  const {
    description,
    partNumber,
    lineCode,
    brand,
    unitCostPrice: yourCost,
    unitCorePrice: corePrice,
    unitListPrice: listPrice,
    quantityShipped: qty,
    locationDescription: location,
    snCalled,
    bundleDiscount,
    bundlePrice,
    bundled,
  } = part
  return {
    thumbnailUrl: undefined, // TODO: ask the API for the thumbnailUrl
    description: description ?? '',
    partNumber: partNumber ?? '',
    lineCode: lineCode ?? '',
    brand: brand ?? '',
    location: location ?? '',
    yourCost: yourCost ?? 0,
    corePrice: corePrice ?? 0,
    listPrice: listPrice ?? 0,
    qty: qty ?? 0,
    total: ((yourCost ?? 0) + (corePrice ?? 0)) * (qty ?? 0),
    snCalled: snCalled ?? '',
    bundleDiscount: bundleDiscount ?? 0,
    bundlePrice: bundlePrice ?? 0,
    bundled: bundled ?? false,
  }
}

export const getPartsTableContent = (order: Order): Array<PartDetail> => {
  const parts = []
  order.parts.forEach((part) => parts.push(getPartDetail(part)))

  return parts
}

export const getOrderConfirmation = (): OrderResponse | undefined => {
  const orderConfirmation = localStorage.getItem(
    StorageKeys.ORDER_CONFIRMATION_KEY
  )
  if (orderConfirmation) {
    return JSON.parse(orderConfirmation)
  }
  return undefined
}

export const setOrderResponses = (response: OrderResponse): void => {
  localStorage.setItem(
    StorageKeys.ORDER_CONFIRMATION_KEY,
    JSON.stringify(response)
  )
}

export const getPartsTotal = (parts: Array<IBundleProduct>): number => {
  return parts.reduce((partAcc, part) => {
    const cost =
      part.bundled && (part.bundlePrice ?? 0) > 0
        ? (part.bundlePrice ?? 0)
        : (part.unitCostPrice ?? 0)
    return partAcc + cost * (part.quantityShipped ?? 0)
  }, 0)
}

export const getPartsCoreTotal = (parts: Array<IBundleProduct>): number => {
  return parts.reduce(
    (partAcc, part) => partAcc + part.unitCorePrice * part.quantityShipped,
    0
  )
}

const getOrderCostSubtotal = (orders: Array<Order>): number => {
  return orders.reduce(
    (orderAcc, ord) => orderAcc + getPartsTotal(ord.parts),
    0
  )
}

const getOrderCoreCostSubtotal = (orders: Array<Order>): number => {
  return orders.reduce(
    (orderAcc, ord) => orderAcc + getPartsCoreTotal(ord.parts),
    0
  )
}

export const getTotalPerOrder = (order: Order): number => {
  const costSubTotal = getPartsTotal(order.parts)
  const coreSubTotal = getPartsCoreTotal(order.parts)
  return costSubTotal + coreSubTotal
}

export const getVehiclesSummaryInfo = (
  orders: Array<OrderResponseDetail>
): Array<VehicleInfoProps> => {
  const vehiclesInfo = orders?.map((order, orderIndex) => {
    const { vehicle: responseVehicle } = order
    const vehicle = {
      ...{
        year: {
          id: Number(responseVehicle.year),
          value: responseVehicle.year,
        },
        make: {
          id: Number(responseVehicle.make),
          value: responseVehicle.make,
        },
        model: {
          id: Number(responseVehicle.model),
          value: responseVehicle.model,
        },
        subModel: {
          id: Number(responseVehicle.subModel),
          value: responseVehicle.subModel,
        },
        engine: {
          id: Number(responseVehicle.engine),
          value: responseVehicle.engine,
        },
      },
    }

    const vehicleHash = StoreInstances.searchStore.getVehicleHash(
      vehicle,
      orderIndex
    )
    const idToCollapse = `collapse-item-totals-${vehicleHash}`
    const anchorTag = `#vehicle_${vehicleHash}`
    const vehicleName = formatVehicleDisplayWithOutEngine(vehicle)
    const costSubTotal: number = order.locations.reduce<number>(
      (acc, loc) => acc + getOrderCostSubtotal(loc.orders),
      0
    )

    const coreSubTotal: number = order.locations.reduce<number>(
      (acc, loc) => acc + getOrderCoreCostSubtotal(loc.orders),
      0
    )

    const laborSubTotal: number =
      StoreInstances.laborStore.calculateLaborCostFromItems(order.laborData)

    return {
      idToCollapse,
      anchorTag,
      vehicleName,
      costSubTotal,
      coreSubTotal,
      laborSubTotal,
    }
  })
  return vehiclesInfo
}

export const getMultiSellerVehiclesSummaryInfo = (
  orders: Array<OrderResponseDetail>
): Array<VehicleInfoProps> => {
  const vehiclesInfo = orders?.map((order, orderIndex) => {
    const { vehicle: responseVehicle, vehicleSummary } = order
    const vehicle = {
      year: {
        id: Number(responseVehicle.year),
        value: responseVehicle.year,
      },
      make: {
        id: Number(responseVehicle.make),
        value: responseVehicle.make,
      },
      model: {
        id: Number(responseVehicle.model),
        value: responseVehicle.model,
      },
      subModel: {
        id: Number(responseVehicle.subModel),
        value: responseVehicle.subModel,
      },
      engine: {
        id: Number(responseVehicle.engine),
        value: responseVehicle.engine,
      },
    }

    const vehicleHash = StoreInstances.searchStore.getVehicleHash(
      vehicle,
      orderIndex
    )
    const idToCollapse = `collapse-item-totals-${vehicleHash}`
    const anchorTag = `#vehicle_${vehicleHash}`
    const vehicleName = formatVehicleDisplayWithOutEngine(vehicle)

    const locationSummaries =
      order?.locations?.map((loc, index) => {
        const summary = vehicleSummary?.locationSummary?.[index]

        return {
          coverageKey: loc.coverageKey,
          costSubTotal: summary?.itemsCost ?? 0,
          coreSubTotal: summary?.coreCost ?? 0,
          bundleSavings: summary?.bundleSavings ?? 0,
          orderTotal: summary?.orderTotal,
        }
      }) || []
    const laborSubTotal = vehicleSummary?.laborCost

    const totalCost = vehicleSummary?.vehicleTotalCost

    return {
      idToCollapse,
      anchorTag,
      vehicleName,
      locationsCostData: locationSummaries,
      totalCost,
      ...(vehicleSummary && { vehicleSummary }),
      laborSubTotal,
    } as VehicleInfoProps
  })

  return vehiclesInfo
}

export const getSummaryGrandTotal = (
  vehiclesInfo: Array<VehicleInfoProps>
): number => {
  return vehiclesInfo?.reduce(
    (acc, cost) =>
      acc +
      (cost.costSubTotal ?? 0) +
      (cost.coreSubTotal ?? 0) +
      (cost.laborSubTotal ?? 0),
    0
  )
}
